<template>
  <MessageBox
    v-if="displayInvoiceBox"
    title="New invoice"
    submit-text="Send invoice"
    type="green"
    :disableSubmit="disableSubmit"
    :submitTooltipText="tooltipText"
    @on-close="setDisplayCreateInvoiceMsgBox(false)"
    @on-cancel="handleCancelInvoice"
    @on-submit="handleSubmitInvoice"
  >
    <div class="message-body-container">
      <div class="message-contents-field">
        <NrSelect
          v-model="selectedInvoiceTemplate"
          :options="templates"
          @input="setMessageTemplate"
          @search="filterInvoiceTemplates"
          placeholder="Select invoice template"
          multiple
        >
          <template v-slot:label>Invoice Template</template>
        </NrSelect>
        <NrFormLabel>Message contents</NrFormLabel>
        <NrEditor
          v-model="message"
          :resizable="false"
          :placeholder="'Type your message here or select a template above.'"
          :rows="rows"
          strike
          bulletList
          orderedList
          link
        />
        <p class="is-size-7" v-if="requesterEmailAvailable">
          This message will be visible to the requester and staff.
        </p>
        <div class="invoice-cost-container">
          <div class="invoice-button-container">
            <NrButton variant="bordered-primary" @click="openAddItemModal">
              <NrIcon class="inline-icon" name="plus" />
              Add item
            </NrButton>

            <NrButton
              variant="bordered-primary"
              @click="setActiveModal('clock')"
            >
              <NrIcon class="inline-icon" name="clock" />
              Add staff hours
            </NrButton>
          </div>
          <div
            v-if="totalSelectedLineItems.length > 0"
            class="invoice-cost-title"
          >
            Line item
          </div>
          <div
            v-if="totalSelectedLineItems.length > 0"
            class="invoice-totals-table"
          >
            <LineItemsTable />
          </div>

          <div
            v-if="Object.values(requestStaffHours).length > 0"
            class="invoice-cost-title"
          >
            Staff hours
          </div>
          <div
            v-if="Object.values(requestStaffHours).length > 0"
            class="invoice-totals-table"
          >
            <StaffHoursTable />
          </div>
          <div
            v-if="
              totalSelectedLineItems.length > 0 ||
              Object.values(requestStaffHours).length > 0
            "
            class="invoice-subtotal"
          >
            <div>Subtotal</div>
            <div>{{ formatCost(subtotalInvoiceAmount) }}</div>
          </div>

          <SurchargesList />
        </div>
        <div
          class="notification is-warning footer"
          v-if="!requesterEmailAvailable"
        >
          <p>
            Requester email not available - posts to timeline only. The
            requester will only be able to see your message if they contact your
            agency to have their email associated with this request.
          </p>
        </div>
      </div>
    </div>
    <div
      :class="[
        'invoice-total-amount',
        negativeInvoiceAmount ? 'negative-total-amount' : ''
      ]"
    >
      <div>Total: {{ formatCost(totalInvoiceAmount) }}</div>
    </div>
  </MessageBox>
  <InvoiceAddItemModal />
</template>

<script lang="ts">
import NrSelect from '@nextrequestco/components/src/components/NrSelect'
import MessageBox from '@nextrequestco/components/src/components/MessageBox'
import NrEditor from '@nextrequestco/components/src/components/NrEditor'
import NrFormLabel from '@nextrequestco/components/src/components/form-elements'
import NrButton from '@nextrequestco/components/src/components/NrButton'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import LineItemsTable from './LineItemsTable.vue'
import SurchargesList from './SurchargesList.vue'
import StaffHoursTable from './StaffHoursTable.vue'
import InvoiceAddItemModal from './InvoiceAddItemModal.vue'
import { mapActions, mapMutations, mapState } from 'vuex'
import {
  InvoiceTemplates,
  InvoiceTemplateOption
} from '../types/invoice_templates'
import {
  SelectedLineItemOption,
  SurchargeOption,
  ApiLineItemPayload,
  ApiSurchargeItemPayload
} from '../types/invoice_line_items'
import helpers from '../../../../../utils/helpers.js'
import truncate from 'lodash/truncate'

export default {
  name: 'InvoiceMessageModal',
  components: {
    MessageBox,
    NrSelect,
    NrEditor,
    NrFormLabel,
    NrButton,
    NrIcon,
    LineItemsTable,
    SurchargesList,
    StaffHoursTable,
    InvoiceAddItemModal
  },

  watch: {
    invoiceTemplates(): void {
      this.templates = this.invoiceTemplates.map((item: InvoiceTemplates) => ({
        value: item.id,
        label: item.invoice_template_name,
        description: item.invoice_template_description
      }))
    },

    displayInvoiceBox(newVal, _): void {
      if (newVal) {
        // Calculate invoice costs on open
        this.recalculateAllCosts()
      }
    },

    requestStaffHours(newVal, _): void {
      if (newVal) {
        this.recalculateAllCosts()
      }
    }
  },

  data() {
    return {
      selectedInvoiceTemplate: [],
      message: '',
      templates: []
    }
  },

  computed: {
    ...mapState(['account']),

    ...mapState('request_show', [
      'requestStaffHours',
      'displayCreateInvoiceMsgBox',
      'currentAction',
      'invoiceTemplates',
      'request',
      'invoiceLineItemType',
      'invoiceLineItemForm',
      'invoiceSelectedSurchargeItem',
      'activeAddItemModal',
      'totalSelectedLineItems',
      'totalSelectedSurchargeItems',
      'subtotalInvoiceAmount',
      'totalInvoiceAmount',
      'invoiceItemToEdit'
    ]),

    tooltipText(): string {
      return this.invalidInvoiceAmount
        ? 'The invoice should total a positive number'
        : ''
    },

    displayInvoiceBox(): boolean {
      return (
        this.displayCreateInvoiceMsgBox && this.currentAction === 'dollar-sign'
      )
    },

    requesterEmailAvailable(): boolean {
      return this.request.requester.email !== 'Email Not Available'
    },

    rows(): number {
      return this.requesterEmailAvailable ? 12 : 8
    },

    negativeInvoiceAmount(): boolean {
      return this.totalInvoiceAmount < 0
    },

    invalidInvoiceAmount(): boolean {
      return this.totalInvoiceAmount <= 0
    },

    disableSubmit(): boolean {
      return !(this.message.length > 0 && this.totalInvoiceAmount > 0)
    }
  },

  methods: {
    ...mapActions('request_show', [
      'setDisplayCreateInvoiceMsgBox',
      'processInvoice',
      'setCurrentAction',
      'recalculateAllCosts'
    ]),

    ...mapMutations('request_show', [
      'setTotalSelectedLineItems',
      'setTotalSelectedSurchargeItems',
      'setSubtotalInvoiceAmount',
      'setTotalInvoiceAmount',
      'setInvoiceItemToEdit',
      'setInvoiceLineItemType',
      'setInvoiceLineItemQuantity',
      'setInvoiceSelectedSurchargeItem',
      'setInvoiceSelectedLineItem',
      'setInvoiceAddItemModal',
      'setActiveModal'
    ]),

    truncate,

    formatCost(amount: number, maximumFractionDigits: number = 2) {
      return helpers.formatCost(amount, maximumFractionDigits)
    },

    openAddItemModal(): void {
      this.resetLineItemState()
      this.setInvoiceItemToEdit(null)
      this.setInvoiceAddItemModal(true)
    },

    resetLineItemState(): void {
      this.setInvoiceLineItemType('')
      this.setInvoiceLineItemQuantity(1)
      this.setInvoiceSelectedSurchargeItem('')
      this.setInvoiceSelectedLineItem('')
    },

    filterInvoiceTemplates(val: string): void {
      if (val === '') {
        this.templates = this.invoiceTemplates.map(
          (item: InvoiceTemplates) => ({
            value: item.id,
            label: item.invoice_template_name,
            description: item.invoice_template_description
          })
        )
        return
      }

      const filteredMsgs = this.invoiceTemplates.filter(
        // eslint-disable-next-line camelcase
        (msg: { invoice_template_name: string }) => {
          const regex = new RegExp(val, 'gi')
          return regex.test(msg.invoice_template_name)
        }
      )

      this.templates = filteredMsgs.map((item: InvoiceTemplates) => ({
        value: item.id,
        label: item.invoice_template_name,
        description: item.invoice_template_description
      }))
    },

    handleCancelInvoice(): void {
      this.message = ''
      this.selectedInvoiceTemplate = []
      this.setDisplayCreateInvoiceMsgBox(false)
    },

    setMessageTemplate(): void {
      this.message = this.selectedInvoiceTemplate
        .map((selected: InvoiceTemplateOption) => {
          return selected.description
        })
        .join(' ')
    },

    totalLineItems(): Array<ApiLineItemPayload> {
      return this.totalSelectedLineItems.map(
        (lineItem: SelectedLineItemOption) => {
          return {
            line_item_template_id: lineItem.value,
            name: lineItem.label,
            cost: Number(lineItem.cost),
            quantity: lineItem.total_count || 1,
            description: lineItem.description
          }
        }
      )
    },

    totalSurcharges(): Array<ApiSurchargeItemPayload> {
      return this.totalSelectedSurchargeItems.map(
        (surcharge: SurchargeOption) => {
          return {
            surcharge_template_id: surcharge.value,
            name: surcharge.label,
            amount: Number(surcharge.amount),
            cost_type: surcharge.cost_type,
            description: surcharge.description
          }
        }
      )
    },

    clearInvoiceLineItems(): void {
      this.setTotalSelectedSurchargeItems([])
      this.setTotalSelectedLineItems([])
      this.setSubtotalInvoiceAmount(0)
      this.setTotalInvoiceAmount(0)
    },

    handleSubmitInvoice(): void {
      const lineItems = this.totalLineItems()
      const surchargeItems = this.totalSurcharges()
      this.processInvoice({
        action: 'create',
        method: 'post',
        message: this.message,
        request_id: this.request.pretty_id,
        invoice_template_id: this.selectedInvoiceTemplate.id,
        total_amount: this.totalInvoiceAmount,
        line_items_attributes: lineItems,
        surcharges_attributes: surchargeItems
      }).then(() => {
        this.handleCancelInvoice()
        this.clearInvoiceLineItems()
      })
    }
  }
}
</script>

<style lang="scss">
@import '@nextrequestco/components/src/styles/colors.scss';
@import '@nextrequestco/components/src/styles/tokens/variables.scss';

.message-box {
  .message-body-container {
    max-height: 520px;
    overflow-y: auto;
  }

  .footer {
    font-size: $font-size-2;
    font-weight: $font-weight-semibold;
    color: $color-purple-110;
    padding-top: $spacing-16;
    padding-bottom: $spacing-16;
    margin-top: $spacing-16;
    margin-bottom: $spacing-16;
  }

  .nr-editor {
    flex: 1 1 auto;
  }

  .invoice-cost-container {
    margin-top: $spacing-12;
  }

  .invoice-button-container {
    display: flex;
    gap: $spacing-16;
    .nr-button {
      width: 50%;
    }
    .nr-icon {
      margin-bottom: $spacing-4;
      margin-right: $spacing-6;
    }
  }

  .invoice-totals-table {
    margin-left: $spacing-24;
    margin-right: $spacing-24;
  }

  .invoice-cost-title {
    margin-left: $spacing-16;
    margin-top: $spacing-16;
  }

  .invoice-subtotal {
    margin-top: $spacing-16;
    margin-left: $spacing-24;
    margin-right: $spacing-36;
    display: flex;
    justify-content: space-between;
    font-weight: $font-weight-semibold;
  }

  .invoice-total-amount {
    margin-top: $spacing-16;
    margin-left: $spacing-24;
    margin-right: $spacing-24;
    font-weight: $font-weight-semibold;
    text-align: right;
  }

  .negative-total-amount {
    color: $color-red-100;
    font-weight: $font-weight-semibold;
  }

  .invoice-surcharge-container {
    background-color: $color-purple-20;
    border-radius: $border-radius-default;

    .invoice-surcharge-row {
      display: flex;
      justify-content: space-between;
      margin-top: $spacing-8;
      margin-right: $spacing-8;
      margin-left: $spacing-24;
      padding-top: $spacing-4;
      padding-bottom: $spacing-4;
    }
  }
}

.align-right {
  text-align: right !important; // Overwrites bulma css style
}

.invoice-cost-title,
.invoice-cost-tr th,
.invoice-cost-tf th,
.invoice-cost-tf tr td {
  font-weight: $font-weight-semibold;
}

.invoice-cost-tf {
  margin-top: $spacing-4;
}

.invoice-totals-table {
  table {
    table-layout: fixed;
    width: 100%;
    border-collapse: separate;
    border-spacing: 0 $spacing-8;
  }
}

.invoice-cost-title,
.invoice-cost-tr th,
.invoice-cost-tf th,
.invoice-cost-tf tr td {
  font-weight: $font-weight-semibold;
}

.invoice-cost-tf {
  margin-top: $spacing-4;
}

.invoice-selected-item-description {
  margin-bottom: $spacing-8;
}
</style>
